import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAppSelector } from 'app/hooks';
import { getBagSizeForCrop } from 'user/selectors';
import { convertForLocale, inferUnit } from 'util/unitConversion';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { FieldData, FormData, RecommendationOptions } from '../../types';
import { parseRecommendationRow } from './utils';

export const useParseRecommendationRows = (fieldData: FieldData) => {
  const { control } = useFormContext<FormData>();
  const bagSize = useAppSelector((state) => getBagSizeForCrop(state, 'corn'));
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const grainPrice = useWatch({
    control,
    name: 'grainPrice',
  });

  const recommendation = fieldData?.recommendation;
  const seedCostPerBag = Number(fieldData?.seedCostPerBag) || 0;

  const convertedFieldTotalArea = convertForLocale({
    quantity: fieldData?.area?.q || 0,
    unit: fieldData?.area?.u || inferUnit('area').unitKey,
    unitType: 'area'
  }).scalar;

  const fvssRow = useMemo(() => {
    if (!recommendation) return null;
    return parseRecommendationRow({
      id: RecommendationOptions.FVSS_RECOMMENDATION,
      seedingRate: recommendation?.recommendation,
      curve: recommendation?.curve,
      convertedFieldTotalArea,
      bagSize,
      seedCostPerBag,
      grainPrice: Number(grainPrice) || 0,
      cropNominalWeight,
      isPreferredPlan: fieldData?.selectedRecommendation === RecommendationOptions.FVSS_RECOMMENDATION
    });
  }, [
    recommendation,
    convertedFieldTotalArea,
    bagSize,
    seedCostPerBag,
    grainPrice,
    cropNominalWeight,
    fieldData?.selectedRecommendation
  ]);

  const yourEntryRow = useMemo(() => {
    if (!recommendation) return null;
    return parseRecommendationRow({
      id: RecommendationOptions.YOUR_ENTRY,
      seedingRate: {
        v: Number(fieldData?.avgFixedPopulation),
        u: inferUnit('seed-density').unitKey
      },
      curve: recommendation?.curve,
      convertedFieldTotalArea,
      bagSize,
      seedCostPerBag,
      grainPrice: Number(grainPrice) || 0,
      cropNominalWeight,
      isPreferredPlan: fieldData?.selectedRecommendation === RecommendationOptions.YOUR_ENTRY
    });
  }, [
    recommendation,
    fieldData?.avgFixedPopulation,
    fieldData?.selectedRecommendation,
    convertedFieldTotalArea,
    bagSize,
    seedCostPerBag,
    grainPrice,
    cropNominalWeight
  ]);

  return { fvssRow, yourEntryRow };
};
